<script setup>
import { ref, reactive, computed, watch, onActivated, nextTick } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { useUI } from '@/composables/useUI'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'
import { PED_VEN_ST } from '@/constants/PED_VEN_ST'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])
const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)
const { obterClasseStatusGhost } = useUI()

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)
const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({ status: [], statusPedido: [], moedas: [] })
const filtros = reactive({ busca: '', lstStatus: '', lstStatusPedido: '' })

const formatarValor = (valor) => {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
}

const formatarBooleano = (val) => val ? 'Sim' : 'Não'

const formatarAtributosJson = (valor) => {
  if (!valor) return '-'
  try {
    const obj = typeof valor === 'string' ? JSON.parse(valor) : valor
    if (!obj || typeof obj !== 'object') return '-'

    // Usa o _templateInfo.atributos para obter os labels
    const templateInfo = obj._templateInfo
    const atributos = templateInfo?.atributos
    if (Array.isArray(atributos) && atributos.length > 0) {
      return atributos
        .map(a => {
          const val = obj[a.chave]
          return `${a.label}: ${val ?? ''}`
        })
        .join(' | ')
    }

    // Fallback: exibe as chaves/valores do objeto (exceto _templateInfo)
    return Object.entries(obj)
      .filter(([chave]) => chave !== '_templateInfo')
      .map(([chave, val]) => `${chave}: ${val ?? ''}`)
      .join(' | ')
  } catch {
    return String(valor)
  }
}

// Resolve o TEXTO amigável do status via Dicionário de Listas (lista PED_VEN_WORKFLOW_ST).
// O código armazenado em lstStatusWorkflowPedido é o idListaClienteItem da lista.
// Comparação sempre por String() (regra do dicionário). Fallback: devolve o próprio código.
const getStatusTexto = (status) => {
  if (!status && status !== '') return '-'
  const item = (listas.status || []).find(s => String(s.idListaClienteItem) === String(status))
  return item ? item.textoLista : status
}

// Resolve o TEXTO amigável do Status do Pedido de Venda via Dicionário de Listas (lista PED_VEN_ST).
// O código armazenado em lstStatusPedido é o idListaClienteItem da lista.
// Comparação sempre por String() (regra do dicionário). Fallback: devolve o próprio código.
const getStatusPedidoTexto = (status) => {
  if (!status && status !== '') return '-'
  const item = (listas.statusPedido || []).find(s => String(s.idListaClienteItem) === String(status))
  return item ? item.textoLista : status
}

// Mapa de cores para o Status do Pedido de Venda (PED_VEN_ST).
// Usa as constantes do arquivo auto-gerado para garantir consistência.
const obterCorStatusPedido = (status) => {
  if (status === PED_VEN_ST.EmAndamento) return 'badge-ghost-warning'
  if (status === PED_VEN_ST.Concluido) return 'badge-ghost-success'
  if (status === PED_VEN_ST.Cancelado) return 'badge-ghost-danger'
  return 'badge-ghost-secondary'
}

const colunasPadrao = [
  // === Default (sempre visíveis) ===
  { field: 'numeroPedidoVenda', label: 'Nº Pedido', width: 150, sortable: true },
  { field: 'nomeCliente', label: 'Cliente', width: 200, sortable: true },
  { field: 'nomeFilial', label: 'Filial', width: 100, sortable: true },
  { field: 'dataEmissao', label: 'Emissão', width: 90, align: 'center', sortable: true },
  { field: 'lstMoeda', label: 'Moeda do Pedido', width: 110, align: 'center', sortable: true },
  { field: 'valorTotalNegociado', label: 'Valor Negociado', width: 90, align: 'right', sortable: true, formatter: (v) => formatarValor(v) },
  { field: 'lstStatusPedido', label: 'Status (Pedido)', width: 130, align: 'center', sortable: true },
  { field: 'lstStatusWorkflowPedido', label: 'Status (Workflow)', width: 140, align: 'center', sortable: true },

  // === Identificação ===
  { field: 'idFilial', label: 'ID Filial', width: 80, align: 'center', sortable: true, visible: false },
  { field: 'idOperacao', label: 'ID Operação', width: 100, align: 'center', sortable: true, visible: false },
  { field: 'numeroContratoVenda', label: 'Contrato Venda', width: 110, sortable: true },
  { field: 'nomeCondicaoPagamento', label: 'Condição Pg (Nacional)', width: 150, sortable: true, visible: false },

  // === Partes Relacionadas ===
  { field: 'nomeRemessa', label: 'Parceiro Remessa Nome', width: 150, sortable: true, visible: false },
  { field: 'idParceiroRemessa', label: 'Parceiro Remessa ID', width: 130, align: 'center', sortable: true, visible: false },
  { field: 'idParceiroNegocioCliente', label: 'Parceiro Cliente ID', width: 130, align: 'center', sortable: true, visible: false },
  { field: 'idParceiroEnderecoFaturamento', label: 'Parceiro End. Fat. ID', width: 140, align: 'center', sortable: true, visible: false },
  { field: 'idParceiroEnderecoEntrega', label: 'Parceiro End. Ent. ID', width: 130, align: 'center', sortable: true, visible: false },
  { field: 'nomeOperacao', label: 'Operação', width: 130, sortable: true, visible: false },

  // === Datas ===
  // === Valores (Moeda Original) ===
  { field: 'valorTotalOriginal', label: 'Vlr Original', width: 120, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'valorTotalDesconto', label: 'Vlr Desconto', width: 110, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'valorTotalFrete', label: 'Vlr Frete', width: 100, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },

  // === Valores (Moeda Padrão) ===
  { field: 'lstMoedaPadrao', label: 'Moeda Padrão', width: 110, align: 'center', sortable: true, visible: false },
  { field: 'valorTotalOriginalPadrao', label: 'Vlr Original (Padrão)', width: 160, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'valorTotalDescontoPadrao', label: 'Vlr Desconto (Padrão)', width: 160, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'valorTotalFretePadrao', label: 'Vlr Frete (Padrão)', width: 150, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'valorTotalNegociadoPadrao', label: 'Vlr Negociado (Padrão)', width: 160, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },

  // === Valores (Moeda Gerencial) ===
  { field: 'lstMoedaGerencial', label: 'Moeda Gerencial', width: 130, align: 'center', sortable: true, visible: false },
  { field: 'valorTotalOriginalGerencial', label: 'Vlr Original (Gerencial)', width: 170, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'valorTotalDescontoGerencial', label: 'Vlr Desconto (Gerencial)', width: 170, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'valorTotalFreteGerencial', label: 'Vlr Frete (Gerencial)', width: 160, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'valorTotalNegociadoGerencial', label: 'Vlr Negociado (Gerencial)', width: 170, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },

  // === Câmbio ===
  { field: 'taxaCambioPadrao', label: 'Taxa Câmbio Padrão', width: 150, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'taxaCambioGerencial', label: 'Taxa Câmbio Pedido/Gerencial', width: 170, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'taxaCambioGerencialPadrao', label: 'Taxa Câmbio Ger./Padrão', width: 160, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },
  { field: 'lstTipoTaxaConversao', label: 'Tipo Taxa Conversão', width: 130, align: 'center', sortable: true, visible: false },
  { field: 'dataAlvoBuscaCambio', label: 'Data Alvo Câmbio', width: 130, align: 'center', sortable: true, visible: false },
  { field: 'dataReferenciaCambio', label: 'Data Ref. Câmbio', width: 130, align: 'center', sortable: true, visible: false },
  { field: 'lstTaxaCambioStatus', label: 'Status Câmbio', width: 120, align: 'center', sortable: true, visible: false },
  { field: 'lstFonteCambio', label: 'Fonte Câmbio', width: 120, sortable: true, visible: false },

  // === Listas / Classificações ===
  { field: 'lstFormaPagamento', label: 'Forma Pagamento', width: 140, align: 'center', sortable: true, visible: false },
  { field: 'lstIncoterm', label: 'Incoterm', width: 100, align: 'center', sortable: true, visible: false },
  { field: 'lstLocalIncoterm', label: 'Local Incoterm', width: 130, sortable: true, visible: false },

  // === Tolerância ===
  { field: 'percentualTolerancia', label: 'Tolerância %', width: 110, align: 'right', sortable: true, formatter: (v) => formatarValor(v), visible: false },

  // === Flags ===
  { field: 'isExportacao', label: 'É Exportação', width: 100, align: 'center', sortable: true, formatter: formatarBooleano, visible: false },

  // === Observação ===
  { field: 'observacaoInterna', label: 'Observação', width: 200, sortable: true, visible: false },

  // === Precificação ===
  { field: 'atributosPrecificacaoJson', label: 'Precificação', width: 250, sortable: true, formatter: formatarAtributosJson, visible: false },

  // === Auditoria ===
  { field: 'nomeUsuarioCriacao', label: 'Criado por', width: 150, sortable: true, visible: false },
  { field: 'nomeUsuarioModificacao', label: 'Modificado por', width: 150, sortable: true, visible: false },
  { field: 'tsCriacao', label: 'Data Criação', width: 160, align: 'center', sortable: true, formatter: (v) => formatarDataHoraBR(v), visible: false },
  { field: 'tsModificacao', label: 'Data Modif.', width: 160, align: 'center', sortable: true, formatter: (v) => formatarDataHoraBR(v), visible: false }
]

const colunas = computed(() => [
  ...colunasPadrao.map(c => {
    const slotMap = {
      dataEmissao: 'dataEmissao',
      lstStatusPedido: 'statusPedido',
      lstStatusWorkflowPedido: 'status'
    }
    return { ...c, slotName: slotMap[c.field] || null }
  }),
  { field: 'acoes', label: 'Ações', width: 70, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
])

const sortResolver = (field, order, data) => {
  // Campos com texto amigável (status) — ordena pelo label de lista, não pelo código
  if (field === 'lstStatusPedido') {
    return [...data].sort((a, b) => {
      const cmp = (getStatusPedidoTexto(a.lstStatusPedido) || '').localeCompare(getStatusPedidoTexto(b.lstStatusPedido) || '', 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  if (field === 'lstStatusWorkflowPedido') {
    return [...data].sort((a, b) => {
      const cmp = (getStatusTexto(a.lstStatusWorkflowPedido) || '').localeCompare(getStatusTexto(b.lstStatusWorkflowPedido) || '', 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }

  // Datas ISO com hora (timestamp) — ordena por instante
  if (field === 'tsCriacao' || field === 'tsModificacao') {
    return [...data].sort((a, b) => {
      const tsA = a[field] ? new Date(a[field]).getTime() : 0
      const tsB = b[field] ? new Date(b[field]).getTime() : 0
      return order === 'asc' ? tsA - tsB : tsB - tsA
    })
  }

  // Campos resolvidos em memória (nomes/descrições que NÃO existem na model PedidoVenda).
  // O backend não consegue ordenar por eles (ApplySorting cai no fallback), então ordena localmente.
  const camposNomes = [
    'nomeCliente', 'nomeFilial', 'nomeOperacao', 'nomeCondicaoPagamento',
    'nomeRemessa', 'numeroContratoVenda', 'nomeUsuarioCriacao', 'nomeUsuarioModificacao',
    'atributosPrecificacaoJson', 'observacaoInterna'
  ]
  if (camposNomes.includes(field)) {
    return [...data].sort((a, b) => {
      const cmp = String(a[field] ?? '').localeCompare(String(b[field] ?? ''), 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }

  // Booleano (É Exportação)
  if (field === 'isExportacao') {
    return [...data].sort((a, b) => {
      const cmp = Number(Boolean(a[field])) - Number(Boolean(b[field]))
      return order === 'asc' ? cmp : -cmp
    })
  }

  // Demais campos são propriedades diretas da model → delegar ao backend (ordenação global)
  return null
}

const handleSortChange = ({ field, order }) => {
  sortField.value = field
  sortOrder.value = order
  buscarDados(1)
}

const confirmationModal = ref(null)
const itemParaExcluir = ref(null)
const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const podeVerAcoes = computed(() => true)

const emitNotification = (p) => emit('show-notification', p)

// ============================================================
// Estado da exportação
// ============================================================
const exportandoProgresso = ref({ total: 0, carregados: 0 })
const dadosExportar = ref(null)

const carregarListas = async () => {
  try {
    const listasSistema = await metadataStore.carregarListasParaTabelas(['PedidoVenda'])
    listas.statusPedido = listasSistema.LstStatusPedido || []
    listas.status = listasSistema.LstStatusWorkflowPedido || []
    listas.moedas = listasSistema.LstMoeda || []
  } catch (e) {
    console.error('Erro ao carregar listas:', e)
  }
}

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    listas.statusPedido = []
    listas.status = []
  }
}, { immediate: true })

const buscarDados = async (pageNumber = 1) => {
  if (!authStore.empresaAtiva) return
  loading.value = true
  erro.value = null
  try {
    const params = injetarParametros(pageNumber)

    params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.lstStatusPedido) params.append('lstStatusPedido', filtros.lstStatusPedido)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)

    const res = await api.get(`/PedidoVendas?${params.toString()}`)
    lista.value = res.data
    atualizarPaginacao(res.headers)
  } catch (e) {
    erro.value = 'Erro ao carregar pedidos de venda.'
    console.error(e)
  } finally {
    loading.value = false
  }
}

const handleAdicionar = () => emit('adicionar-item')
const handleEditar = (item) => emit('editar-item', item)

const handleExcluir = async () => {
  if (!itemParaExcluir.value) return
  try {
    await api.delete(`/PedidoVendas/${itemParaExcluir.value.idPedidoVenda}`)
    emit('show-notification', { message: 'Pedido de venda excluído com sucesso.', type: 'success' })
    buscarDados(1)
  } catch (e) {
    emit('show-notification', { message: 'Erro ao excluir pedido de venda.', type: 'error' })
  }
  itemParaExcluir.value = null
}

const handlePageChange = (page) => buscarDados(page)
const handleFiltrar = () => buscarDados(1)
const handleLimparFiltros = () => {
  filtros.busca = ''
  filtros.lstStatusPedido = ''
  filtros.lstStatus = ''
  buscarDados(1)
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idPedidoVenda
  auditoriaSelecionada.nome = `Pedido Venda #${item.numeroPedidoVenda}`
  mostrarAuditoria.value = true
}

// ============================================================
// Exportação de dados (AdminTable)
// ============================================================
async function handleExportarDados () {
  if (!authStore.empresaAtiva) {
    emitNotification({ message: 'Selecione uma empresa antes de exportar.', type: 'warning' })
    return
  }

  const totalRegistros = paginacao.value.totalCount ?? paginacao.value.TotalCount ?? 0
  if (!totalRegistros) {
    emitNotification({ message: 'Nenhum dado para exportar.', type: 'warning' })
    return
  }

  exportandoProgresso.value = { total: totalRegistros, carregados: 0 }
  dadosExportar.value = null

  const pageSize = paginacao.value.PageSize || 25
  const totalPages = Math.ceil(totalRegistros / pageSize)
  let todosDados = []

  try {
    for (let page = 1; page <= totalPages; page++) {
      const params = new URLSearchParams()
      params.append('pageNumber', page)
      params.append('pageSize', pageSize)

      params.append('idEmpresa', authStore.empresaAtiva)
      if (sortField.value) params.append('sortField', sortField.value)
      if (sortOrder.value) params.append('sortOrder', sortOrder.value)
      if (filtros.busca) params.append('busca', filtros.busca)
      if (filtros.lstStatusPedido) params.append('lstStatusPedido', filtros.lstStatusPedido)
      if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)

      const response = await api.get(`/PedidoVendas?${params.toString()}`)
      const dados = response.data
      if (Array.isArray(dados)) {
        todosDados = todosDados.concat(dados)
      }

      exportandoProgresso.value = { total: totalRegistros, carregados: todosDados.length }
    }

    dadosExportar.value = [...todosDados]

    await nextTick()
    exportandoProgresso.value = { total: 0, carregados: 0 }
  } catch (e) {
    console.error('[PedidoVendaAdministrar] Erro na exportação:', e)
    emitNotification({ message: 'Erro ao coletar dados para exportação.', type: 'error' })
    exportandoProgresso.value = { total: 0, carregados: 0 }
  }
}

onActivated(() => {
  if (authStore.empresaAtiva) buscarDados(paginacao.PageNumber || 1)
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Pedidos de Venda"
    :loading="loading"
    :erro="erro"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="handlePageChange"
    @mudar-tamanho-pagina="(s) => { paginacao.PageSize = s; buscarDados(1) }"
    @adicionar="handleAdicionar"
    @atualizar="handleFiltrar"
    :mostrar-botao-adicionar="podeVerAcoes"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <div class="row g-2 align-items-end">
        <div class="col-md-4">
          <label class="form-label small mb-1">Busca</label>
          <input class="form-control form-control-sm" v-model="filtros.busca" placeholder="Nº Pedido ou Cliente..." @keyup.enter="handleFiltrar" />
        </div>
        <div class="col-md-3">
          <label class="form-label small mb-1">Status (Pedido)</label>
          <select class="form-select form-select-sm" v-model="filtros.lstStatusPedido">
            <option value="">Todos</option>
            <option v-for="s in (listas.statusPedido || [])" :key="s.idListaClienteItem" :value="s.idListaClienteItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label small mb-1">Status (Workflow)</label>
          <select class="form-select form-select-sm" v-model="filtros.lstStatus">
            <option value="">Todos</option>
            <option v-for="s in (listas.status || [])" :key="s.idListaClienteItem" :value="s.idListaClienteItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-md-2 d-flex gap-1">
          <button class="btn btn-sm btn-outline-primary" @click="handleFiltrar">Filtrar</button>
          <button class="btn btn-sm btn-outline-secondary" @click="handleLimparFiltros">Limpar</button>
        </div>
      </div>
    </template>

    <AdminTable
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum pedido de venda encontrado."
      row-key="idPedidoVenda"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="handleEditar($event.row)"
      modulo-layout="PedidoVenda"
      :colunas-padrao="colunasPadrao"
      :colunas-fixas="['acoes']"
      :resolver-listas-tabela="'PedidoVenda'"
      :exportando-progresso="exportandoProgresso"
      :dados-exportar="dadosExportar"
      titulo-exportacao="Pedidos de Venda"
      @show-notification="emitNotification"
      @exportar-dados="handleExportarDados"
    >
      <template #numeroPedidoVenda="{ row }">
        <span class="fw-medium text-body-emphasis">{{ row.numeroPedidoVenda }}</span>
      </template>

      <template #dataEmissao="{ row }">
        {{ formatarDataBR(row.dataEmissao) }}
      </template>

      <template #statusPedido="{ row }">
        <span class="badge" :class="obterCorStatusPedido(row.lstStatusPedido)">
          {{ getStatusPedidoTexto(row.lstStatusPedido) }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterClasseStatusGhost(getStatusTexto(row.lstStatusWorkflowPedido))">
          {{ getStatusTexto(row.lstStatusWorkflowPedido) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon text-primary" @click="handleEditar(row)" title="Editar Pedido de Venda">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>

    <template #paginacao>
      <nav v-if="paginacao.TotalPages > 1">
        <ul class="pagination pagination-sm justify-content-center mb-0">
          <li class="page-item" :class="{ disabled: paginacao.PageNumber <= 1 }">
            <button class="page-link" @click="handlePageChange(paginacao.PageNumber - 1)">Anterior</button>
          </li>
          <li v-for="p in paginacao.TotalPages" :key="p" class="page-item" :class="{ active: p === paginacao.PageNumber }">
            <button class="page-link" @click="handlePageChange(p)">{{ p }}</button>
          </li>
          <li class="page-item" :class="{ disabled: paginacao.PageNumber >= paginacao.TotalPages }">
            <button class="page-link" @click="handlePageChange(paginacao.PageNumber + 1)">Próximo</button>
          </li>
        </ul>
      </nav>
    </template>

  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="PedidoVenda"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />

  <ConfirmationModal ref="confirmationModal" title="Confirmar Exclusão" message="Tem certeza que deseja excluir este pedido de venda?" @confirm="handleExcluir" />
</template>
